import { load } from 'cheerio';
import axios from 'axios';
import dayjs from 'dayjs';
import he from 'he';

/**
 * Waits for window.adsAreWithUs to be defined (prebid.js loaded)
 * @param {number} maxWait - Maximum time to wait in ms (default 2000ms)
 * @param {number} interval - Polling interval in ms (default 50ms)
 * @returns {Promise<boolean>} - Resolves with the value of adsAreWithUs (true/false/undefined after timeout)
 */
export const waitForAdsCheck = (maxWait = 2000, interval = 200) => {
	return new Promise((resolve) => {
		// If already defined, resolve immediately
		if (typeof window.adsAreWithUs !== 'undefined') {
			resolve(window.adsAreWithUs);
			return;
		}

		let elapsed = 0;
		const timer = setInterval(() => {
			elapsed += interval;
			if (typeof window.adsAreWithUs !== 'undefined') {
				clearInterval(timer);
				resolve(window.adsAreWithUs);
			} else if (elapsed >= maxWait) {
				clearInterval(timer);
				// Timeout - assume no ads (adblocker or script failed)
				resolve(false);
			}
		}, interval);
	});
};
/**
 * Coerce a WordPress field to its FLAT string form.
 *
 * The canonical S3 shape is now flat: `title`/`content`/`excerpt` are plain
 * strings (matching the Songbird webhook and the flattened buckets). Legacy
 * build-time files may still carry the `{ rendered }` object form, so unwrap
 * those here to keep the migration safe regardless of which pipeline produced
 * a given file. Pages read these fields directly (no `.rendered`).
 */
const toFlatRendered = (value) => {
	if (typeof value === "string") return value;
	if (value && typeof value === "object" && typeof value.rendered === "string") {
		return value.rendered;
	}
	return value === undefined ? undefined : "";
};

/**
 * Normalize a per-slug S3 payload (produced by the Songbird `store-single-post`
 * webhook / `process-single-news-item.ts`, or a legacy build-time file left
 * over from the retired `utils/fetch-posts-create-dictionary.js` script)
 * into the shape the page templates expect.
 *
 * - `title`/`content`/`excerpt` are guaranteed FLAT strings (legacy `{ rendered }`
 *   objects are unwrapped) so templates can read them directly.
 * - `_embedded["wp:featuredmedia"][0].source_url` is synthesized from
 *   `featured_image` so the existing media logic keeps working without a
 *   WordPress REST round-trip.
 *
 * Idempotent: flat strings and an existing `_embedded` are passed through.
 */
export const normalizeS3PostToWpShape = (s3Data) => {
	if (!s3Data || typeof s3Data !== "object") return s3Data;

	const post = { ...s3Data };

	// Guarantee flat string fields regardless of source pipeline.
	if (post.title !== undefined) post.title = toFlatRendered(post.title);
	if (post.content !== undefined) post.content = toFlatRendered(post.content);
	if (post.excerpt !== undefined) post.excerpt = toFlatRendered(post.excerpt);

	if (s3Data._embedded) {
		post._embedded = s3Data._embedded;
		return post;
	}

	const featured = s3Data.featured_image || s3Data.image;
	if (typeof featured === "string" && featured.length > 0) {
		// `featured_image` is stored as a CDN-proxied URL of the form:
		//   https://content.timothysykes.com/cdn-cgi/image/<opts>/<rawSourceUrl>
		// Strip the proxy prefix so consumers that re-apply the proxy don't
		// double-wrap the URL.
		const rawSourceUrl = featured.replace(
			/^https:\/\/content\.timothysykes\.com\/cdn-cgi\/image\/[^/]+\//,
			""
		);

		post._embedded = {
			"wp:featuredmedia": [
				{
					source_url: rawSourceUrl,
					media_details: {
						width: 740,
						height: 532
					}
				}
			]
		};
	} else {
		post._embedded = { "wp:featuredmedia": [] };
	}

	return post;
};

export const calculateReadingTime = (content) => {
	// Assuming an average reading speed of 200 words per minute
	const wordsPerMinute = 200;

	// Count the number of words in the content
	const wordCount = content.split(/\s+/).length;

	// Calculate the estimated reading time in minutes
	const readingTimeMinutes = wordCount / wordsPerMinute;

	// Round up the reading time to the nearest whole number
	const roundedReadingTime = Math.ceil(readingTimeMinutes);

	return roundedReadingTime;
};

export const imageLinkFormatter = (
	link,
	width = 456,
	quality = 70,
	env,
	hostname
) => {
	if (!link) return;
	if (env === "PRODUCTION")
		if (!link.includes("/cdn-cgi/image/")) {
			const trimmed_link = link.split(".com")[1];

			const img_format = link.includes(".gif") ? "webp" : "webp";

			return `${hostname}/cdn-cgi/image/quality=${quality},format=${img_format},width=${width}/${hostname}${trimmed_link}`;
		}
	return link;
};

const CF_IMAGE_PROXY_RE = /https?:\/\/[^/\s]+\/cdn-cgi\/image\/[^/\s]+\//g;
const DEFAULT_CF_IMAGE_HOST = "https://content.timothysykes.com";
// Hotlinked uploads from sister WP sites that share the same media paths.
const MEDIA_HOST_ALIASES = ["https://smartdatatrading.com", "http://smartdatatrading.com"];

/**
 * Strip one or more Cloudflare Image Resizing prefixes so consumers can
 * re-apply a single proxy layer without nesting
 * (`/cdn-cgi/image/.../cdn-cgi/image/.../source.jpg`).
 */
export const stripCfImageProxy = (url) => {
	if (!url || typeof url !== "string") return url;

	let result = url.trim();
	let previous;
	do {
		previous = result;
		result = result.replace(CF_IMAGE_PROXY_RE, "");
	} while (result !== previous);

	return result;
};

/**
 * Point sister-site upload URLs at the Timothy Sykes content host so CF
 * Image Resizing and hotlink-sensitive browsers can fetch them.
 */
export const normalizeMediaHostname = (
	url,
	targetHost = DEFAULT_CF_IMAGE_HOST
) => {
	if (!url || typeof url !== "string") return url;

	const target = String(targetHost).replace(/\/$/, "");
	let result = url;
	for (const alias of MEDIA_HOST_ALIASES) {
		if (result.includes(`${alias}/wp-content/uploads/`)) {
			result = result.replace(alias, target);
		}
	}
	return result;
};

/**
 * Apply a single Cloudflare Image Resizing prefix. Idempotent for already-
 * proxied (including nested) URLs.
 */
export const applyCfImageProxy = (
	url,
	{
		width = 740,
		quality = 90,
		hostname = DEFAULT_CF_IMAGE_HOST
	} = {}
) => {
	if (!url || typeof url !== "string") return url;

	const raw = normalizeMediaHostname(stripCfImageProxy(url), hostname);
	if (!raw) return url;

	const base = String(hostname).replace(/\/$/, "");
	return `${base}/cdn-cgi/image/quality=${quality},format=webp,width=${width}/${raw}`;
};

/**
 * Rewrite a srcset so each candidate has exactly one CF image proxy layer.
 * Uses the `Nw` descriptor as the resize width when present.
 *
 * Avoids the old hostname-global replace, which nested proxies whenever
 * Songbird (or a prior pass) had already wrapped the URL.
 */
export const rewriteSrcsetWithCfProxy = (
	srcset,
	{
		quality = 90,
		hostname = DEFAULT_CF_IMAGE_HOST,
		fallbackWidth = 740
	} = {}
) => {
	if (!srcset || typeof srcset !== "string") return srcset;

	// Split on comma+whitespace between candidates only. CF options use
	// commas WITHOUT spaces (`quality=100,format=webp,width=598`), so a
	// bare `.split(",")` would shred already-proxied URLs.
	return srcset
		.split(/,\s+/)
		.map((part) => {
			const trimmed = part.trim();
			if (!trimmed) return "";

			const [url, ...rest] = trimmed.split(/\s+/);
			if (!url) return "";

			const descriptor = rest.join(" ");
			const widthMatch = descriptor.match(/^(\d+)w$/i);
			const width = widthMatch ? widthMatch[1] : fallbackWidth;

			const proxied = applyCfImageProxy(url, { width, quality, hostname });
			return descriptor ? `${proxied} ${descriptor}` : proxied;
		})
		.filter(Boolean)
		.join(", ");
};

export const getLyteWrapperFields = (content) => {
	let lyte_fields = {};

	const $ = load(content, {
		decodeEntities: false, // Prevent HTML entity decoding
		xmlMode: false // Do not treat input as XML
	});

	// Try the original selector first
	const lyteElement = $('.lyMe.hidef');
	if (lyteElement.length > 0 && lyteElement[0]?.attribs?.['id']) {
		const childId = lyteElement[0]?.children?.[0]?.attribs?.['id'];
		if (childId && childId.includes('lyte_')) {
			lyte_fields = {
				el_id: lyteElement[0].attribs['id'],
				yt_id: childId.split('lyte_')[1],
			};
		}
	}

	// Fallback: try alternative selectors for different Lyte plugin versions
	if (!lyte_fields.yt_id) {
		// Try finding element with lyte class
		const lyteAlt = $('.lyte');
		if (lyteAlt.length > 0) {
			const el = lyteAlt[0];
			const elId = el?.attribs?.['id'];
			if (elId) {
				// Extract YouTube ID from the element ID (format: WYL_YOUTUBEID)
				const match = elId.match(/WYL_([a-zA-Z0-9_-]+)/);
				if (match) {
					lyte_fields = {
						el_id: elId,
						yt_id: match[1],
					};
				}
			}
		}
	}

	return lyte_fields;
};

export const imageLinkFormatterForPost = (link) => {
	return imageLinkFormatter(link, 786);
};

export const youtubeEmbedParser = () => {
	const lyte_wrappers = document.querySelectorAll(".lyte-wrapper");

	if (lyte_wrappers.length > 0) {
		[...lyte_wrappers].forEach(function (el, index) {
			// WordPress Lyte Wrapper - YouTube
			if (el.hasAttribute("data-parsed")) {
				return;
			}
			el.setAttribute("data-parsed", true);

			const lyte_fields = getLyteWrapperFields(el.innerHTML);

			const element_with_image = el.querySelector("[data-src]");

			if (element_with_image) {
				const img_src = element_with_image.getAttribute("data-src");
				const proxiedSrc = applyCfImageProxy(img_src);
				element_with_image.style.backgroundImage = `url("${proxiedSrc}")`;
				element_with_image.style.backgroundSize = "cover";

				const windowWidth = window.innerWidth;
				if (windowWidth <= 920) {
					element_with_image.style.backgroundPosition = "center";
					element_with_image.style.minHeight = "216px";
					element_with_image.style.position = "relative";
					const play_btn = el.querySelector(".play");
					if (play_btn) {
						play_btn.style.backgroundImage = "url(../images/lytesprite.png)";
						play_btn.style.width = "72px";
						play_btn.style.height = "72px";
						play_btn.style.bottom = "0";
						play_btn.style.left = "0";
						play_btn.style.margin = "auto";
						play_btn.style.right = "0";
						play_btn.style.position = "absolute";
						play_btn.style.backgroundPositionX = "-11px";
						play_btn.style.top = "0";
					}
				}
			}

			const yt_id = lyte_fields["yt_id"];
			const el_id = lyte_fields["el_id"];

			// Skip if no YouTube ID was found
			if (!yt_id) {
				return;
			}

			const yt_iframe =
				'<iframe id="iF_' +
				yt_id +
				'" width="840" height="236" ' +
				'src="https://www.youtube-nocookie.com/embed/' +
				yt_id +
				"?autoplay=1&playsinline=1&" +
				'controls=1&wmode=opaque&rel=0&egm=0&iv_load_policy=3&hd=1&vq=hd720" ' +
				'frameborder="0" style="width: 100%;" allowfullscreen="" allow="autoplay; fullscreen"></iframe>';

			// Function to replace content with iframe
			const replaceWithIframe = function (evt) {
				evt.preventDefault();
				evt.stopPropagation();

				// Prevent double-firing on devices that trigger both touch and click
				if (el.hasAttribute("data-video-loaded")) {
					return;
				}
				el.setAttribute("data-video-loaded", "true");

				el.innerHTML = yt_iframe;
				setTimeout(() => {
					const iframe = el.querySelector('iframe');
					if (iframe) {
						iframe.style.width = "100%";
					}
				}, 400);
			};

			// Try to attach event listeners to the specific element ID first
			if (el_id) {
				const targetElement = document.querySelector('[id="' + el_id + '"]');
				if (targetElement) {
					// Add both click and touch events for mobile compatibility
					targetElement.addEventListener("click", replaceWithIframe);
					targetElement.addEventListener("touchend", replaceWithIframe);
					targetElement.style.cursor = "pointer";
				}
			}

			// Always attach event listeners to the wrapper itself as fallback
			el.addEventListener("click", replaceWithIframe);
			el.addEventListener("touchend", replaceWithIframe);
			el.style.cursor = "pointer";
		});
	}

	const allHaveAttribute = Array.from(lyte_wrappers).every((el) =>
		el.hasAttribute("data-foo")
	);

	if (allHaveAttribute) return "close";
};

export const onClickOnAnchorScrollToTitle = (e) => {
	setTimeout(() => {
		const el_y_position = window.scrollY;
		window.scrollTo(0, el_y_position - 100);
	}, 25);
};

export const addTwitterEmbedd = () => {
	const twitterScript = document.createElement("script");
	twitterScript.setAttribute("src", "https://platform.twitter.com/widgets.js");
	twitterScript.setAttribute("defer", true);

	const body = document.querySelector("body");

	body.appendChild(twitterScript);
};

export function modifyAndSubmitForm(emailInputId) {
	const emailInput = document.getElementById(emailInputId);

	if (!emailInput) {
		return false;
	}

	const emailValue = emailInput.value;

	//URL encoding
	const modifiedEmailValue = emailValue.replace(/\+/g, "%2B");

	//Hidden Input
	emailInput.value = modifiedEmailValue;

	return true;
}

export const fetchAuthorByName = async (hostname, authorName) => {
	try {
		const randomParam = Math.random().toString(36).substring(7);

		const response = await fetch(`${hostname}wp-json/biographies/v1/author-metadata/all?random=${randomParam}`, {
			method: "GET"
		});

		if (response.status === 200) {
			const customAuthors = await response.json();
			const author_name = authorName.replace("-", " ");

			const author = customAuthors.find((author) => author.name.toLowerCase() === author_name)

			return author;
		} else {
			console.error("Custom authors request failed:", response.statusText);
			return null;
		}
	} catch (error) {
		console.error("Error loading custom authors:", error);
		return null;
	}
};


export const getYoutubeLink = (content) => {
	const $ = load(content, {
		decodeEntities: false, // Prevent HTML entity decoding
		xmlMode: false // Do not treat input as XML
	});

	let ytObj;
	$("div").each((_index, ytDiv) => {
		if ($(ytDiv).attr("class") === "lyte-wrapper") {
			if (!ytObj) {
				const youtubeId = ytDiv.children[0].attribs.id.split('_')[1];

				ytObj = {
					thumbnailUrl: `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`,
					contentUrl: `https://www.youtube.com/watch?v=${youtubeId}"`,
					embedUrl: `https://www.youtube.com/watch?v=${youtubeId}"`
				}

				return null;
			}
		}
	});

	return ytObj;
};

export const external_links_icon = `<svg class="external-icon"  fill="#3b82f6" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h560v-280h80v280q0 33-23.5 56.5T760-120H200Zm188-212-56-56 372-372H560v-80h280v280h-80v-144L388-332Z"/></svg>`;

export const shuffleArray = (array) => {
	for (let i = array.length - 1; i > 0; i--) {
		// Generate random index
		const j = Math.floor(Math.random() * (i + 1));
		// Swap elements at indices i and j
		[array[i], array[j]] = [array[j], array[i]];
	}
	return array;
}


export function getCookie(name) {
	const cookies = document.cookie.split(";");
	for (const cookie of cookies) {
		const [cookieName, cookieValue] = cookie.split("=");
		if (cookieName.trim() === name) {
			return decodeURIComponent(cookieValue);
		}
	}
	return null;
}

export const checkCookieConsent = () => {
	const matches = Boolean(getCookie("cookieConsent"));
	return matches;
};



export const injectWatchlistStyle = async (className, time = 100) => {
	await setTimeout(() => {
		const element = document.querySelector(`.${className}`);
		if (element) {
			const style = document.createElement("style");
			style.innerHTML = `
			.author-watchlist-information-title {
				padding-top: 24px;
				padding-bottom: 0px;
				font-size: 22px;
				font-weight: 700;
				line-height: 29px;
				letter-spacing: 0em;
				font-family: Rubik !important;
				text-align: left !important;
			}

			.author-watchlist-information-subtitle {
				padding-top: 8px 0px;
				font-size: 14px;
				font-weight: 400;
				line-height: 24px;
				letter-spacing: 0em;
				text-align: left !important;
				color: #1e293b;
				font-family: Rubik !important;
				display: inline-block;
			}

			.author-watchlist-information-email-input {
				margin-bottom: 10px;
				padding: 8px;
			}

			.author-watchlist-information-subscribe-button {
				background-color: #ef6820;
				color: #fff;
				font-weight: 700;
				font-size: 16px;
				border: none;
				border-radius: 8px;
				padding: 10px 20px;
				font-size: 16px;
				cursor: pointer;
				position: relative;
				transition: background-color 0.3s ease;
				width: fit-content;
				display: block;
				margin-top: 12px;
				font-family: Rubik !important;
			}

			.author-watchlist-information-subscribe-button::after {
				font-size: 24px;
				width: 20px;
				height: 14.14px;
				top: 3px;
			}

			.author-watchlist-information-description {
				font-size: 14px;
			}

			.author-watchlist-image {
				width: 100%;
			}

			.author-watchlist-image img {
				width: 100%;
				height: auto;
				-o-object-fit: cover;
				object-fit: cover;
				border-radius: 4px;
			}

			.author-side-box {
				border-radius: 24px;
				background-color: white;
				display: flex;
				flex-direction: column;
				padding: 20px;
				gap: 20px;
				box-shadow: 0px 7px 20px 0px #0000001a;
				margin-left: 0;
				width: 100%;
				height: auto;
				padding: 20px;
				box-shadow: 0px 7px 20px 0px #0000001a;
			}

			@media (max-width: 768px) {
				.author-side-box {
					margin-left: 0;
					width: 100%;
					height: auto;
					padding: 20px;
					box-shadow: 0px 7px 20px 0px #0000001a;
				}
			}
			`;
			document.head.appendChild(style);
			element.style.display = "block";
		}
	}, time);
}


export async function adbutlerTargetBlankInjector(timer) {
	await setTimeout(() => {
		const adbutler_link = document.querySelectorAll(
			"a.author-watchlist-information-subscribe-button, .end-of-article-ad a, .related-post-ad a, .author-watchlist-image a, .author-watchlist-information-title a"
		);

		[...adbutler_link].forEach((link) => {
			link?.setAttribute("target", "_blank");
		});
	}, timer)
}

export function isValidJSON(input) {
	if (typeof input === 'string') {
		try {
			JSON.parse(input);
			return true;
		} catch {
			return false;
		}
	}
	return false; // Neither a JSON string nor a valid object
};


export function sttChartCreator(STOCK_SYMBOL) {
	const dependecyScript = document.createElement('script');
	dependecyScript.src = 'https://wdgt01.stockstotrade.com:8082/js/compiled/embede/sttwidget.min.js';

	const chartBody = document.createElement('div');
	chartBody.setAttribute("id", `SttAdvancedEquote__${STOCK_SYMBOL}_ID`);

	const magicHappensScript = document.createElement('script');
	magicHappensScript.innerHTML = `
	setTimeout(function() {

		var objectSttAdvancedEquote = new SttAdvancedEquote().create({
			symbol: "${STOCK_SYMBOL}",
			source: "delayedotc_bats",
			allowPanning: 1,
			dateFormat: "US",
			period: "1m",
			maxCandles: 30,
			width: "100%",
			height: 600,
			affiliateCode: "12345",
			productCode: "123456789",
			period: "1y",
			productParams: "utm_source=source&utm_medium=medium&utm_campaign=campaign",
			asynchTarget: "SttAdvancedEquote__${STOCK_SYMBOL}_ID",
		});
			},250)

		`;

	return {
		dependecyScript,
		magicHappensScript,
		chartBody
	}
}

// eslint-disable-next-line require-await
export const fetchFallbackAdbutlerBanner = async (ad_name, ad_container_selector, isModal, backendUrl) => {
	try {
		const response = await axios.get(`${backendUrl}?adname=${ad_name}`);

		if (response.status === 200) {
			const adbutler_container = document.querySelectorAll(ad_container_selector);

			for (let i = 0; i < adbutler_container.length; i++) {
				const container = adbutler_container[i];

				if (container) {

					const $ = load(response.data.data.html, {
						decodeEntities: false, // Prevent HTML entity decoding
						xmlMode: false // Do not treat input as XML
					});

					// $("img").each((_index, imgElement) => {
					// })
					// $("p").each((_index, pElement) => {
					// })

					if (isModal) {
						container.classList.remove("!hidden");
						$("script").remove();
					}
					const adContent = $.html();
					if (adContent) {
						const decodedContent = he.decode(adContent);
						container.insertAdjacentHTML('beforeend', decodedContent);
					}

					return true
				}
			}
		}
	} catch (error) {
		throw new Error(`Error fetching Adbutler Ad - ${ad_name}`, { cause: error });
	}
}

export const checkForCookieAndSwipeUp = () => {
	const fab = document.querySelector('.left-fab');
	if (fab) {
		const cookieConsent = document.querySelector('.cookie-consent');
		const swipeUp = document.querySelector('.slide-message');

		if (swipeUp && !fab.classList.contains('site-has-message')) {
			fab.classList.add('site-has-message');
		}
		if (cookieConsent && !fab.classList.contains('has-cookie-consent')) {
			fab.classList.add('has-cookie-consent');
		}
	}
}

export function daysAgo(date, isOnPage = false) {

	const dateObj = dayjs(date);
	const now = dayjs();
	const diff = now.diff(dateObj);

	if (isOnPage) {
		if (diff < 1000 * 60 * 60) {
			const minutes = Math.ceil(diff / 1000 / 60);
			return `${minutes} min${minutes >= 2 ? 's' : ''} ago`;
		} else if (diff < 1000 * 60 * 60 * 24) {
			const hours = Math.ceil(diff / 1000 / 60 / 60);
			return `${hours} hour${hours >= 2 ? 's' : ''} ago`;
		}
		const days = Math.ceil(diff / 1000 / 60 / 60 / 24);
		return `${days} day${days >= 2 ? 's' : ''} ago`;
	}

	if (diff < 1000 * 60) {
		const minutes = Math.ceil(diff / 1000 / 60);
		return `${minutes} min${minutes >= 2 ? 's' : ''} ago`;
	} else if (diff < 1000 * 60 * 60) {
		const hours = Math.ceil(diff / 1000 / 60 / 60);
		return `${hours} hour${hours >= 2 ? 's' : ''} ago`;
	} else {
		const days = Math.ceil(diff / 1000 / 60 / 60 / 24);
		return `${days} day${days >= 2 ? 's' : ''} ago`;
	}
}